import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button, Modal, Form, Row, Col, Alert } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";
import { createOnlineBooking, updateOnlineBooking } from "../../acommsapi";
import { uploadImage, updatePracticeURL } from "../../api";

// Initial state factory to avoid repeated object creation
const createInitialBookingState = () => ({
  selectedPractices: [],
  bookingHeading: "",
  logo: [],
  locations: [],
  allowOverPadding: false,
  allowOverlap: false,
  overlapMinutes: "",
  leadingMinutes: "",
  serviceCatalog: [""],
});

function AddEditOnlineBookings({
  onlineBookingDetail,
  onOnlineBookingUpdate = () => {},
  ...props
}) {
  const [user, setUser] = useState({});
  const [practices, setPractices] = useState([]);
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState();
  // Validation state
  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);

  // Consolidated state object to reduce re-renders
  const [bookingState, setBookingState] = useState(createInitialBookingState);

  // Destructure for easier access
  const {
    selectedPractices,
    bookingHeading,
    allowOverPadding,
    allowOverlap,
    overlapMinutes,
    leadingMinutes,
    serviceCatalog: serviceCatalogList,
  } = bookingState;

  // Memoized isEditing flag
  const isEditing = useMemo(
    () => Boolean(onlineBookingDetail?._id),
    [onlineBookingDetail?._id]
  );

  // Initialize user and practices data
  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (token) {
      const decoded = jwtDecode(token);
      setUser(decoded);
      getUserPracticeById(decoded._id).then((userPractices) => {
        setPractices(userPractices);
      });
    }
  }, []);

  // Handle online booking detail changes
  useEffect(() => {
    if (onlineBookingDetail) {
      setBookingState({
        selectedPractices: onlineBookingDetail.selectedPractices || [],
        bookingHeading: onlineBookingDetail.bookingHeading || "",
        logo: onlineBookingDetail.logo || [],
        locations: onlineBookingDetail.locations || [],
        allowOverPadding: onlineBookingDetail.allowOverPadding || false,
        allowOverlap: onlineBookingDetail.allowOverlap || false,
        overlapMinutes: onlineBookingDetail.overlapMinutes || "",
        leadingMinutes: onlineBookingDetail.leadingMinutes || "",
        serviceCatalog: onlineBookingDetail.serviceCatalog?.length
          ? onlineBookingDetail.serviceCatalog
          : [""],
      });
      
      // Set preview image if logo exists
      if (onlineBookingDetail.logo && onlineBookingDetail.logo.length > 0) {
        // Assuming the logo array contains URLs or file paths
        setPreviewImage(onlineBookingDetail.logo[0]);
      }
    } else {
      setBookingState(createInitialBookingState());
      setPreviewImage(null);
    }
  }, [onlineBookingDetail]);

  // Handlers
  const handlePracticeChange = useCallback(
    (e) => {
      const value = e.target.value;
      setBookingState((prev) => ({
        ...prev,
        selectedPractices: prev.selectedPractices.includes(value)
          ? prev.selectedPractices.filter((p) => p !== value)
          : [...prev.selectedPractices, value],
      }));
      // Clear validation error when user selects a practice
      if (errors.practices) {
        setErrors((prev) => ({ ...prev, practices: undefined }));
      }
    },
    [errors.practices]
  );

  const handleBookingHeadingChange = useCallback(
    (e) => {
      const value = e.target.value;
      setBookingState((prev) => ({ ...prev, bookingHeading: value }));
      // Clear validation error when user types
      if (errors.bookingHeading) {
        setErrors((prev) => ({ ...prev, bookingHeading: undefined }));
      }
    },
    [errors.bookingHeading]
  );

  const handleLogoChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setSelectedFile(file);
      setLogoFile(file);

      // Update preview with selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setSelectedFile(null);
    setLogoFile(null);
    setPreviewImage(null);
    setBookingState((prev) => ({ ...prev, logo: [] }));
    // Clear the file input
    const fileInput = document.getElementById('logoUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleBooleanChange = useCallback(
    (field) => (e) => {
      setBookingState((prev) => ({ ...prev, [field]: e.target.checked }));
    },
    []
  );

  const handleOverlapMinutesChange = useCallback(
    (e) => {
      const value = e.target.value;
      const input = value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
      let formattedDuration = input;

      // Format the input into HH:mm format
      if (input.length > 2) {
        formattedDuration = `${input.slice(0, 2)}:${input.slice(2, 4)}`;
      }

      // Restrict the length to HH:mm format (5 characters max)
      if (formattedDuration.length > 5) {
        formattedDuration = formattedDuration.slice(0, 5);
      }

      setBookingState((prev) => ({
        ...prev,
        overlapMinutes: formattedDuration,
      }));
      // Clear validation error when user types
      if (errors.overlapMinutes) {
        setErrors((prev) => ({ ...prev, overlapMinutes: undefined }));
      }
    },
    [errors.overlapMinutes]
  );

   const handleLeadingMinutesChange = useCallback(
    (e) => {
      const value = e.target.value;
      const input = value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
      let formattedDuration = input;

      // Format the input into HH:mm format
      if (input.length > 2) {
        formattedDuration = `${input.slice(0, 2)}:${input.slice(2, 4)}`;
      }

      // Restrict the length to HH:mm format (5 characters max)
      if (formattedDuration.length > 5) {
        formattedDuration = formattedDuration.slice(0, 5);
      }

      setBookingState((prev) => ({
        ...prev,
        leadingMinutes: formattedDuration,
      }));
      // Clear validation error when user types
      if (errors.leadingMinutes) {
        setErrors((prev) => ({ ...prev, leadingMinutes: undefined }));
      }
    },
    [errors.leadingMinutes]
  );

  const handleServiceCatalogChange = useCallback(
    (index, value) => {
      setBookingState((prev) => ({
        ...prev,
        serviceCatalog: prev.serviceCatalog.map((catalog, i) =>
          i === index ? value : catalog
        ),
      }));
      // Clear validation error when user types
      if (errors.serviceCatalog) {
        setErrors((prev) => ({ ...prev, serviceCatalog: undefined }));
      }
    },
    [errors.serviceCatalog]
  );

  const removeServiceCatalog = useCallback((index) => {
    setBookingState((prev) => ({
      ...prev,
      serviceCatalog: prev.serviceCatalog.filter((_, i) => i !== index),
    }));
  }, []);

  const addServiceCatalog = useCallback(() => {
    setBookingState((prev) => ({
      ...prev,
      serviceCatalog: [...prev.serviceCatalog, ""],
    }));
  }, []);

  // Fixed validation logic
  const validateForm = () => {
    const newErrors = {};

    // Validate practices selection
    if (selectedPractices.length === 0) {
      newErrors.practices = "Please select at least one practice.";
    }

    // Validate booking heading
    if (!bookingHeading.trim()) {
      newErrors.bookingHeading = "Online Booking Page Heading is required.";
    }

    // Validate overlap minutes format (only when allowOverlap is checked)
    if (
      allowOverlap &&
      overlapMinutes &&
      !/^\d{2}:\d{2}$/.test(overlapMinutes)
    ) {
      newErrors.overlapMinutes =
        "Enter duration in HH:mm format (e.g., 00:15).";
    }

     if (
      leadingMinutes &&
      !/^\d{2}:\d{2}$/.test(leadingMinutes)
    ) {
      newErrors.leadingMinutes =
        "Enter duration in HH:mm format (e.g., 00:15).";
    }

    // Validate service catalog - at least one non-empty entry
    if (serviceCatalogList.every((type) => !type.trim())) {
      newErrors.serviceCatalog = "At least one service type should be added.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setValidated(true);

      const isValid = validateForm();
      if (!isValid) {
        return;
      }

      try {
        let logoUrl = bookingState.logo; // Keep existing logo if no new file
        
        // Handle logo upload if a file was selected
        if (logoFile && selectedFile) {
          const formData = new FormData();
          formData.append("image", selectedFile);
          
          try {
            const uploadResponse = await uploadImage(formData);
            logoUrl = [uploadResponse.imageUrl]; // Use the returned URL
            
            // Optional: Update practice logo as well
            // await updatePracticeLogoIfNeeded(uploadResponse.imageUrl, selectedPractices);
          } catch (error) {
            console.error("Image upload failed:", error);
            alert("Image upload failed. Please try again.");
            return; // Don't proceed with success if image upload fails
          }
        }

        const updatedBooking = {
          ...bookingState,
          logo: logoUrl,
        };

        if (isEditing) {
          await updateOnlineBooking(onlineBookingDetail._id, updatedBooking);
          // Update the booking with the new logo URL if we uploaded a new one
          if (logoFile && logoUrl !== bookingState.logo) {
            await updateOnlineBooking(onlineBookingDetail._id, { ...updatedBooking, logo: logoUrl });
          }
        } else {
          await createOnlineBooking(updatedBooking);
        }

        setSuccessAlert(true);
        setTimeout(() => {
          setSuccessAlert(false);
          props.onHide();
          onOnlineBookingUpdate();
        }, 1000);
      } catch (error) {
        console.error("Error saving online booking settings:", error);
        alert("Failed to save booking settings. Please try again.");
      }
    },
    [
      bookingState,
      logoFile,
      selectedFile,
      isEditing,
      onlineBookingDetail?._id,
      props,
      onOnlineBookingUpdate,
      selectedPractices,
      bookingHeading,
      allowOverlap,
      overlapMinutes,
      serviceCatalogList,
    ]
  );

  const handleClose = useCallback(() => {
    setCloseAlert(true);
  }, []);

  const handleCancelNo = useCallback(() => {
    setCloseAlert(false);
  }, []);

  const handleCancelYes = useCallback(() => {
    setCloseAlert(false);
    props.onHide();
    setBookingState(createInitialBookingState());
    setErrors({});
    setValidated(false);
    setPreviewImage(null);
    setLogoFile(null);
    setSelectedFile(null);
  }, [props]);

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="online-booking-modal-title"
      centered
      onHide={handleClose}
    >
      <Modal.Header closeButton={!closeAlert && !successAlert}>
        <Modal.Title id="online-booking-modal-title" className="text-primary">
          {isEditing
            ? "Edit Online Booking Settings"
            : "Add Online Booking Settings"}
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert
          variant="info"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>
            Are you sure you want to close? Changes will not be saved.
          </span>
          <div className="ms-auto d-flex">
            <Button
              variant="outline-primary"
              className="me-1"
              onClick={handleCancelYes}
            >
              Yes
            </Button>
            <Button variant="outline-secondary" onClick={handleCancelNo}>
              No
            </Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert
          variant="success"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>Online booking settings saved successfully!</span>
        </Alert>
      )}

      <Modal.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group controlId="selectedPractices" className="mb-3">
                <Form.Label className="mb-0">
                  <strong>Select Practices:</strong>
                </Form.Label>
                <br />
                <p className="text-muted mb-3">
                  Select the practices for which you want to enable online
                  bookings. Their locations will be displayed as well.
                </p>
                <div className={errors.practices ? "is-invalid" : ""}>
                  {practices.map((practice) => (
                    <Form.Check
                      key={practice.practiceNumber}
                      type="checkbox"
                      label={practice.practiceName}
                      value={practice.practiceNumber}
                      checked={selectedPractices.includes(
                        practice.practiceNumber
                      )}
                      onChange={handlePracticeChange}
                      className="mb-1"
                    />
                  ))}
                </div>
                {errors.practices && (
                  <div className="invalid-feedback d-block">
                    {errors.practices}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Row className="mb-3">
                <Form.Group controlId="logoUpload">
                  <Form.Label className="mb-0">
                    <strong>Upload Booking Page Logo</strong>
                  </Form.Label>
                  <br />
                  <p className="text-muted mb-3">
                    If no logo is uploaded the logo of the first practice on the
                    list will be used.
                  </p>
                  <Form.Control
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleLogoChange}
                    id="logoUpload"
                  />
                  {previewImage && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Logo Preview:</small>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveLogo}
                        >
                          Remove Logo
                        </Button>
                      </div>
                      <img
                        src={previewImage}
                        alt="Logo preview"
                        style={{
                          maxHeight: "200px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="bookingHeading">
                  <Form.Label className="mb-0">
                    <strong>Booking Page Heading</strong>
                  </Form.Label>
                  <br />
                  <p className="text-muted mb-3">
                    This will be displayed at the top of the booking page.
                  </p>
                  <Form.Control
                    type="text"
                    value={bookingHeading}
                    onChange={handleBookingHeadingChange}
                    isInvalid={!!errors.bookingHeading}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bookingHeading}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>

          <hr />

          <Row>
            <Form.Label className="mb-3 mt-0">
              <strong>Appointment Settings:</strong>
            </Form.Label>
            <br />
            <Col md={6}>
              <Form.Group controlId="allowOverPadding">
                <Form.Check
                  type="checkbox"
                  label="Bookings can be made over padding time"
                  checked={allowOverPadding}
                  onChange={handleBooleanChange("allowOverPadding")}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="allowOverlap">
                <Form.Check
                  type="checkbox"
                  label="Allow booking overlap"
                  checked={allowOverlap}
                  onChange={handleBooleanChange("allowOverlap")}
                />
              </Form.Group>
              <p className="text-muted mb-3">
                Allow online bookings to overlap with existing bookings with a
                set amount of minutes.
              </p>
              {allowOverlap && (
                <Form.Group controlId="overlapMinutes">
                  <Form.Label>Enter overlap minutes (HH:mm)</Form.Label>
                  <Form.Control
                    type="text"
                    className="w-25"
                    placeholder="e.g. 00:15"
                    value={overlapMinutes}
                    onChange={handleOverlapMinutesChange}
                    isInvalid={!!errors.overlapMinutes}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.overlapMinutes}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Col>
            <Row className="mt-3">
              <Col md={6}>
               <Form.Group controlId="leadingMinutes">
                  <Form.Label className="mb-1">Enter lead time (HH:mm)</Form.Label>
                  <p className="text-muted mb-0">
                The minimum time before an appointment that a booking can be made.
              </p>
                  <Form.Control
                    type="text"
                    className="w-25"
                    placeholder="e.g. 00:15"
                    value={leadingMinutes}
                    onChange={handleLeadingMinutesChange}
                    isInvalid={!!errors.leadingMinutes}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.leadingMinutes}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Row>

          <hr />

          <Row>
            <Col md={12}>
              <Form.Group controlId="serviceCatalog" className="mb-3">
                <Form.Label>
                  <strong>Service Catalog Types</strong>
                </Form.Label>
                {serviceCatalogList.map((catalog, index) => (
                  <Row key={index} className="mb-2 align-items-center">
                    <Col xs={10}>
                      <Form.Control
                        type="text"
                        placeholder={`Type ${index + 1}`}
                        value={catalog}
                        onChange={(e) =>
                          handleServiceCatalogChange(index, e.target.value)
                        }
                        isInvalid={!!errors.serviceCatalog && index === 0}
                      />
                    </Col>
                    <Col xs={2}>
                      <Button
                        variant="outline-danger"
                        onClick={() => removeServiceCatalog(index)}
                        disabled={serviceCatalogList.length <= 1}
                      >
                        <i className="fa fa-trash text-danger" />
                      </Button>
                    </Col>
                  </Row>
                ))}
                {errors.serviceCatalog && (
                  <div className="invalid-feedback d-block">
                    {errors.serviceCatalog}
                  </div>
                )}
                {serviceCatalogList.length < 4 && (
                  <Button variant="secondary" onClick={addServiceCatalog}>
                    Add Type
                  </Button>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-center align-items-center w-100">
          <Button
            variant="primary"
            className="px-5 py-2"
            type="submit"
            onClick={handleSubmit}
          >
            {isEditing ? "Save Changes" : "Submit"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AddEditOnlineBookings;